import { roundToStrike } from "@/lib/options/structure";
import type { Mode, Regime } from "@/types/options";
import { defineStrategy } from "./define";
import { leg } from "./leg";
import { offsetParam, toggleParam } from "./params";
import { scale } from "./scale";

// ── Ratio Backspread ──────────────────────────────────────────────────────
// Sell 1 near-ATM, buy 2 further OTM. Call/put toggle.

export const ratioBackspread = defineStrategy(
  "ratio_backspread",
  "Ratio Backspread",
  "ratio",
  {
    defaultParams: () => ({
      optionType: 0,
      shortOffset: 10,
      longOffset: 30,
    }),
    paramSchema: [
      toggleParam("optionType", "Type", [
        [0, "Call"],
        [1, "Put"],
      ]),
      offsetParam("shortOffset", "Short Strike Offset", 0, 60, 0),
      offsetParam("longOffset", "Long Strike Offset", 5, 100, 1),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      optionType: cur.optionType ?? 0,
      shortOffset: scale(10, r, m, true),
      longOffset: scale(30, r, m, false),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      if (params.optionType >= 1) {
        // Put: sell 1 higher put, buy 2 lower puts
        return [
          leg(center - params.shortOffset, "put", "short"),
          leg(center - params.longOffset, "put", "long", 2),
        ];
      }
      // Call: sell 1 lower call, buy 2 higher calls
      return [
        leg(center + params.shortOffset, "call", "short"),
        leg(center + params.longOffset, "call", "long", 2),
      ];
    },
  },
);

// ── Ratio Spread ──────────────────────────────────────────────────────────
// Buy 1 near-ATM, sell 2 further OTM. Call/put toggle.

export const ratioSpread = defineStrategy(
  "ratio_spread",
  "Ratio Spread",
  "ratio",
  {
    defaultParams: () => ({
      optionType: 0,
      longOffset: 10,
      shortOffset: 30,
    }),
    paramSchema: [
      toggleParam("optionType", "Type", [
        [0, "Call"],
        [1, "Put"],
      ]),
      offsetParam("longOffset", "Long Strike Offset", 0, 60, 0),
      offsetParam("shortOffset", "Short Strike Offset", 5, 100, 1),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      optionType: cur.optionType ?? 0,
      longOffset: scale(10, r, m, false),
      shortOffset: scale(30, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      if (params.optionType >= 1) {
        // Put: buy 1 higher put, sell 2 lower puts
        return [
          leg(center - params.longOffset, "put", "long"),
          leg(center - params.shortOffset, "put", "short", 2),
        ];
      }
      // Call: buy 1 lower call, sell 2 higher calls
      return [
        leg(center + params.longOffset, "call", "long"),
        leg(center + params.shortOffset, "call", "short", 2),
      ];
    },
  },
);

// ── Bull Call Ladder ───────────────────────────────────────────────────────
// Buy 1 ATM/ITM call, sell 1 higher, sell 1 even higher.

export const bullCallLadder = defineStrategy(
  "bull_call_ladder",
  "Bull Call Ladder",
  "ratio",
  {
    defaultParams: () => ({
      longOffset: 0,
      short1Offset: 20,
      short2Offset: 40,
    }),
    paramSchema: [
      offsetParam("longOffset", "Long Offset", -20, 40, 0),
      offsetParam("short1Offset", "Short 1 Offset", 5, 60, 1),
      offsetParam("short2Offset", "Short 2 Offset", 10, 100, 2),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      longOffset: scale(0, r, m, false, 40),
      short1Offset: scale(20, r, m, true),
      short2Offset: scale(40, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center + params.longOffset, "call", "long"),
        leg(center + params.short1Offset, "call", "short"),
        leg(center + params.short2Offset, "call", "short"),
      ];
    },
  },
);

// ── Bear Call Ladder ──────────────────────────────────────────────────────
// Sell 1 lower call, buy 1 middle call, buy 1 higher call.

export const bearCallLadder = defineStrategy(
  "bear_call_ladder",
  "Bear Call Ladder",
  "ratio",
  {
    defaultParams: () => ({
      shortOffset: 10,
      long1Offset: 30,
      long2Offset: 50,
    }),
    paramSchema: [
      offsetParam("shortOffset", "Short Offset", 0, 60, 0),
      offsetParam("long1Offset", "Long 1 Offset", 5, 80, 1),
      offsetParam("long2Offset", "Long 2 Offset", 15, 120, 2),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      shortOffset: scale(10, r, m, true),
      long1Offset: scale(30, r, m, false),
      long2Offset: scale(50, r, m, false),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center + params.shortOffset, "call", "short"),
        leg(center + params.long1Offset, "call", "long"),
        leg(center + params.long2Offset, "call", "long"),
      ];
    },
  },
);

// ── Bull Put Ladder ───────────────────────────────────────────────────────
// Sell 1 higher put, buy 1 middle put, buy 1 lower put.

export const bullPutLadder = defineStrategy(
  "bull_put_ladder",
  "Bull Put Ladder",
  "ratio",
  {
    defaultParams: () => ({
      shortOffset: 10,
      long1Offset: 30,
      long2Offset: 50,
    }),
    paramSchema: [
      offsetParam("shortOffset", "Short Offset", 0, 60, 0),
      offsetParam("long1Offset", "Long 1 Offset", 5, 80, 1),
      offsetParam("long2Offset", "Long 2 Offset", 15, 120, 2),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      shortOffset: scale(10, r, m, true),
      long1Offset: scale(30, r, m, false),
      long2Offset: scale(50, r, m, false),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center - params.shortOffset, "put", "short"),
        leg(center - params.long1Offset, "put", "long"),
        leg(center - params.long2Offset, "put", "long"),
      ];
    },
  },
);

// ── Bear Put Ladder ───────────────────────────────────────────────────────
// Buy 1 ATM/ITM put, sell 1 lower put, sell 1 even lower put.

export const bearPutLadder = defineStrategy(
  "bear_put_ladder",
  "Bear Put Ladder",
  "ratio",
  {
    defaultParams: () => ({
      longOffset: 0,
      short1Offset: 20,
      short2Offset: 40,
    }),
    paramSchema: [
      offsetParam("longOffset", "Long Offset", -20, 40, 0),
      offsetParam("short1Offset", "Short 1 Offset", 5, 60, 1),
      offsetParam("short2Offset", "Short 2 Offset", 10, 100, 2),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      longOffset: scale(0, r, m, false, 40),
      short1Offset: scale(20, r, m, true),
      short2Offset: scale(40, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center - params.longOffset, "put", "long"),
        leg(center - params.short1Offset, "put", "short"),
        leg(center - params.short2Offset, "put", "short"),
      ];
    },
  },
);
